export const CAROUSEL_MIN_CARDS = 2;
export const CAROUSEL_MAX_CARDS = 10;

/**
 * 🔴 "single" SPLIT INTO "image" AND "video". One option labelled "Single image
 * or video" left the uploader accepting either, so an ad's kind was only
 * decided by whichever file was dropped on it. Picking the kind up front lets
 * AdMediaField reject the wrong one at the field instead of at publish.
 *
 * Safe to change: this value never leaves the browser. The create payload
 * writes the literal `format: "carousel"` for a carousel and omits the field
 * otherwise, and drafts store that payload rather than this state.
 */
export type AdFormat = "image" | "video" | "carousel";

export interface CarouselCard {
  key: string;
  headline: string;
  description: string;
  link: string;
  image_hash: string;
  image_url: string;
  video_id: string;
  preview: string;
  uploading: boolean;
  video_state: "" | "processing" | "ready" | "failed";
  media_error: string;
  media_label: string;
  /** The file's own size and date, for the editor's media row. Neither is
      published: CarouselCardPayload lists what Meta receives. */
  media_size: number;
  media_date: string;
  cover_label: string;
  cover_size: number;
  cover_dims: string;
  cover_date: string;
}

export interface CarouselCardPayload {
  headline?: string;
  description?: string;
  link?: string;
  image_hash?: string;
  image_url?: string;
  video_id?: string;
}

let cardSeq = 0;

export function newCarouselCard(partial: Partial<CarouselCard> = {}): CarouselCard {
  cardSeq += 1;
  return {
    key: `card-${Date.now().toString(36)}-${cardSeq}`,
    headline: "",
    description: "",
    link: "",
    image_hash: "",
    image_url: "",
    video_id: "",
    preview: "",
    uploading: false,
    video_state: "",
    media_error: "",
    media_label: "",
    media_size: 0,
    media_date: "",
    cover_label: "",
    cover_size: 0,
    cover_dims: "",
    cover_date: "",
    ...partial,
  };
}

export function initialCarouselCards(): CarouselCard[] {
  return Array.from({ length: CAROUSEL_MIN_CARDS }, () => newCarouselCard());
}

export function normalizeWebUrl(value: string): string {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(text) && !/^[^/:]+\.[^/:]+:\d+/.test(text)) return text;
  return `https://${text.replace(/^\/+/, "")}`;
}

export function isWebUrl(value: string): boolean {
  const text = String(value ?? "").trim();
  if (!text || /\s/.test(text)) return false;
  try {
    const url = new URL(text);
    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    const host = url.hostname;
    return host === "localhost" || /^[^.]+(\.[^.]+)+$/.test(host);
  } catch {
    return false;
  }
}

export function webUrlError(value: string): string | null {
  const text = String(value ?? "").trim();
  if (!text) return null;
  return isWebUrl(normalizeWebUrl(text)) ? null : "Enter a website address like wenext.ai or https://wenext.ai/shop";
}

export function cardHasMedia(card: CarouselCard): boolean {
  return !!(card.image_hash || card.video_id || (card.image_url && isWebUrl(card.image_url)));
}

export function validateCarouselCards(
  cards: readonly CarouselCard[],
  { needsLinks }: { needsLinks: boolean },
): string | null {
  if (cards.length < CAROUSEL_MIN_CARDS) return `A carousel needs at least ${CAROUSEL_MIN_CARDS} cards.`;
  if (cards.length > CAROUSEL_MAX_CARDS) return `A carousel can have at most ${CAROUSEL_MAX_CARDS} cards.`;
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    const label = `Card ${i + 1}`;
    if (card.uploading) return `${label} is still uploading.`;
    if (!cardHasMedia(card)) return card.media_error ? `${label}: ${card.media_error}` : `${label} needs an image or a video.`;
    if (card.video_id && card.video_state === "failed") return `${label}: Meta could not process this video. Upload a different file.`;
    if (card.video_id && card.video_state === "processing") return `${label}: Meta is still processing the video. Try again in a minute.`;
    if (!card.headline.trim()) return `${label} needs a headline.`;
    if (needsLinks && !isWebUrl(normalizeWebUrl(card.link))) {
      return `${label} needs a website address, like wenext.ai`;
    }
  }
  return null;
}

export function carouselCardsPayload(
  cards: readonly CarouselCard[],
  { needsLinks }: { needsLinks: boolean },
): CarouselCardPayload[] {
  return cards.map((card) => {
    const out: CarouselCardPayload = { headline: card.headline.trim() };
    const description = card.description.trim();
    if (description) out.description = description;
    if (needsLinks) out.link = normalizeWebUrl(card.link);
    if (card.video_id) {
      out.video_id = card.video_id;
      if (card.image_hash) out.image_hash = card.image_hash;
    } else if (card.image_hash) {
      out.image_hash = card.image_hash;
    } else if (card.image_url) {
      out.image_url = card.image_url;
    }
    return out;
  });
}

export function reorderCarouselCards(cards: readonly CarouselCard[], from: number, to: number): CarouselCard[] {
  if (from === to || from < 0 || to < 0 || from >= cards.length || to >= cards.length) return [...cards];
  const next = [...cards];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export function moveCarouselCard(cards: readonly CarouselCard[], index: number, delta: -1 | 1): CarouselCard[] {
  const target = index + delta;
  if (index < 0 || index >= cards.length || target < 0 || target >= cards.length) return [...cards];
  const next = [...cards];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function removeCarouselCard(cards: readonly CarouselCard[], key: string): CarouselCard[] {
  if (cards.length <= CAROUSEL_MIN_CARDS) return [...cards];
  return cards.filter((card) => card.key !== key);
}

export function addCarouselCard(cards: readonly CarouselCard[]): CarouselCard[] {
  if (cards.length >= CAROUSEL_MAX_CARDS) return [...cards];
  return [...cards, newCarouselCard()];
}

export function patchCarouselCard(
  cards: readonly CarouselCard[],
  key: string,
  patch: Partial<CarouselCard>,
): CarouselCard[] {
  return cards.map((card) => (card.key === key ? { ...card, ...patch } : card));
}

type CreativeChild = {
  name?: unknown;
  description?: unknown;
  link?: unknown;
  image_hash?: unknown;
  picture?: unknown;
  image_url?: unknown;
  video_id?: unknown;
};

export function carouselCardsFromCreative(creative: unknown): CarouselCardPayload[] {
  const children = (creative as { object_story_spec?: { link_data?: { child_attachments?: unknown } } } | null)
    ?.object_story_spec?.link_data?.child_attachments;
  if (!Array.isArray(children)) return [];
  return (children as CreativeChild[]).map((child) => ({
    headline: child?.name ? String(child.name) : undefined,
    description: child?.description ? String(child.description) : undefined,
    link: child?.link ? String(child.link) : undefined,
    image_hash: child?.image_hash ? String(child.image_hash) : undefined,
    image_url: child?.picture ? String(child.picture) : child?.image_url ? String(child.image_url) : undefined,
    video_id: child?.video_id ? String(child.video_id) : undefined,
  }));
}
